import { useState, useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { stateWiseWork, impactTimeline, impactCategories, annualReports, financialReports } from '@/mocks/impactData';
import { impactCounters } from '@/mocks/homeData';

export default function ImpactPage() {
  const [activeTab, setActiveTab] = useState<'statistics' | 'timeline' | 'states' | 'reports'>('statistics');
  const [animatedCounters, setAnimatedCounters] = useState<number[]>(impactCounters.map(() => 0));
  const [visibleCategory, setVisibleCategory] = useState<number | null>(null);
  const countersRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          impactCounters.forEach((counter, i) => {
            const duration = 2000;
            const steps = 60;
            const increment = counter.value / steps;
            let current = 0;
            const interval = setInterval(() => {
              current += increment;
              if (current >= counter.value) {
                setAnimatedCounters((prev) => {
                  const next = [...prev];
                  next[i] = counter.value;
                  return next;
                });
                clearInterval(interval);
              } else {
                setAnimatedCounters((prev) => {
                  const next = [...prev];
                  next[i] = Math.floor(current);
                  return next;
                });
              }
            }, duration / steps);
          });
        }
      },
      { threshold: 0.3 }
    );

    if (countersRef.current) observer.observe(countersRef.current);
    return () => observer.disconnect();
  }, []);

  const tabs = [
    { key: 'statistics' as const, label: 'Statistics', icon: 'ri-bar-chart-box-line' },
    { key: 'timeline' as const, label: 'Timeline', icon: 'ri-history-line' },
    { key: 'states' as const, label: 'State-wise Work', icon: 'ri-map-pin-line' },
    { key: 'reports' as const, label: 'Reports', icon: 'ri-file-chart-line' },
  ];

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-20 pb-12 md:pt-28 md:pb-16 bg-gradient-to-br from-primary-500 via-primary-400 to-accent-400 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/4 blur-3xl"></div>
          <div className="relative max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <span className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur-sm text-white/90 text-xs font-medium rounded-full mb-4">
              <i className="ri-bar-chart-box-line mr-1.5"></i>Our Impact
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4">Every Number Tells a Story</h1>
            <p className="text-white/80 text-sm md:text-lg max-w-2xl mx-auto">
              From our first 50 children in a single village to over 25,000 lives transformed — explore the journey, data, and real impact behind every donation.
            </p>
          </div>
        </section>

        {/* Live Counter Stats */}
        <section ref={countersRef} className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
              {impactCounters.map((counter, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 md:p-6 border border-background-200 text-center group hover:border-primary-200 transition-colors">
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${i % 3 === 0 ? 'bg-primary-100 text-primary-600' : i % 3 === 1 ? 'bg-accent-100 text-accent-600' : 'bg-secondary-100 text-secondary-600'}`}>
                    <i className={`${counter.icon} text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950 tabular-nums">
                    {animatedCounters[i].toLocaleString()}{counter.suffix}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500 mt-1">{counter.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tab Navigation */}
        <section className="sticky top-16 md:top-20 z-30 bg-white/90 backdrop-blur-xl border-b border-background-200">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex overflow-x-auto -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Tab Content */}
        <section className="py-10 md:py-14 bg-background-50 min-h-[500px]">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-8">

            {/* Statistics Tab */}
            {activeTab === 'statistics' && (
              <div className="space-y-6">
                {impactCategories.map((cat, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-background-200 overflow-hidden">
                    <button
                      onClick={() => setVisibleCategory(visibleCategory === i ? null : i)}
                      className="w-full flex items-center justify-between p-5 md:p-6 text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${i % 3 === 0 ? 'bg-primary-100 text-primary-600' : i % 3 === 1 ? 'bg-accent-100 text-accent-600' : 'bg-secondary-100 text-secondary-600'}`}>
                          <i className={`${cat.icon} text-xl`}></i>
                        </div>
                        <div>
                          <h3 className="font-heading text-lg font-bold text-foreground-950">{cat.name}</h3>
                          <p className="text-foreground-500 text-sm">{cat.beneficiaries.toLocaleString()} beneficiaries · {cat.projects} projects</p>
                        </div>
                      </div>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                        visibleCategory === i ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-500'
                      }`}>
                        <i className={`text-sm transition-transform duration-300 ${visibleCategory === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                      </div>
                    </button>
                    <div
                      className={`transition-all duration-300 overflow-hidden ${
                        visibleCategory === i ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="px-5 md:px-6 pb-5 md:pb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {cat.stats.map((stat, j) => (
                          <div key={j} className="p-4 bg-background-50 rounded-xl border border-background-200">
                            <div className="flex items-center gap-2">
                              <i className="ri-check-line text-green-600 text-sm"></i>
                              <span className="text-sm font-semibold text-foreground-900">{stat}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className="relative">
                <div className="absolute left-5 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {impactTimeline.map((item, i) => (
                    <div key={i} className={`relative flex flex-col md:flex-row gap-4 md:gap-0 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="md:w-1/2"></div>
                      <div className="absolute left-[14px] md:left-1/2 top-0 w-3 h-3 rounded-full bg-primary-500 border-4 border-white md:-translate-x-1.5 z-10"></div>
                      <div className={`md:w-1/2 pl-10 md:pl-0 ${i % 2 === 0 ? 'md:pl-10' : 'md:pr-10 md:text-left'}`}>
                        <div className="bg-white rounded-2xl p-5 border border-background-200 hover:border-primary-200 transition-colors">
                          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-bold rounded-full mb-2">{item.year}</span>
                          <h3 className="font-heading text-base font-bold text-foreground-950 mb-1.5">{item.title}</h3>
                          <p className="text-foreground-500 text-sm leading-relaxed mb-3">{item.description}</p>
                          <div className="flex items-center gap-2 text-xs text-primary-600 font-semibold">
                            <i className="ri-star-fill text-xs"></i>
                            {item.highlight}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* State-wise Tab */}
            {activeTab === 'states' && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl p-5 md:p-6 border border-background-200 mb-6">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-1">Our Reach Across India</h3>
                  <p className="text-foreground-500 text-sm">Active in 150+ cities across 12 states with 2,500+ dedicated volunteers.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {stateWiseWork.map((state, i) => (
                    <div key={i} className="bg-white rounded-2xl p-5 border border-background-200 hover:border-primary-200 transition-colors group cursor-pointer">
                      <div className="flex items-start justify-between mb-3">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${state.color} text-white`}>
                          <i className={`${state.icon} text-lg`}></i>
                        </div>
                        <span className="text-xs font-bold text-foreground-400 bg-background-100 px-2.5 py-1 rounded-full">{state.cities} cities</span>
                      </div>
                      <h4 className="font-heading text-base font-bold text-foreground-950 mb-1">{state.state}</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed mb-3">{state.description}</p>
                      <div className="flex flex-wrap gap-3 text-xs">
                        <span className="flex items-center gap-1 text-foreground-600">
                          <i className="ri-folder-line text-foreground-400"></i> {state.projects} projects
                        </span>
                        <span className="flex items-center gap-1 text-foreground-600">
                          <i className="ri-user-line text-foreground-400"></i> {state.beneficiaries.toLocaleString()} beneficiaries
                        </span>
                        <span className="flex items-center gap-1 text-foreground-600">
                          <i className="ri-team-line text-foreground-400"></i> {state.volunteers} volunteers
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reports Tab */}
            {activeTab === 'reports' && (
              <div className="space-y-10">
                {/* Annual Reports */}
                <div>
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-5 flex items-center gap-2">
                    <i className="ri-book-open-line text-primary-500"></i> Annual Reports
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {annualReports.map((report, i) => (
                      <div key={i} className="bg-white rounded-2xl p-5 border border-background-200 hover:border-primary-200 transition-colors cursor-pointer group">
                        <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-3 group-hover:bg-primary-200 transition-colors">
                          <i className="ri-file-pdf-line text-primary-600 text-xl"></i>
                        </div>
                        <h4 className="text-sm font-bold text-foreground-950 mb-1">{report.title}</h4>
                        <p className="text-xs text-foreground-500 mb-3">{report.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-foreground-400 bg-background-100 px-2 py-1 rounded-md">{report.size}</span>
                          <span className="flex items-center gap-1 text-xs font-semibold text-primary-600">
                            <i className="ri-download-line"></i> Download
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Financial Reports */}
                <div>
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-5 flex items-center gap-2">
                    <i className="ri-file-chart-line text-accent-500"></i> Financial Reports
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {financialReports.map((report, i) => (
                      <div key={i} className="bg-white rounded-2xl p-5 border border-background-200 hover:border-accent-200 transition-colors cursor-pointer group">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-200 transition-colors">
                            <i className="ri-file-text-line text-accent-600 text-xl"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-bold text-foreground-950 mb-1">{report.title}</h4>
                            <p className="text-xs text-foreground-500 mb-3">{report.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-foreground-400 bg-background-100 px-2 py-1 rounded-md">{report.size}</span>
                              <span className="flex items-center gap-1 text-xs font-semibold text-accent-600">
                                <i className="ri-download-line"></i> Download
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-14 md:py-18 bg-gradient-to-r from-primary-500 to-accent-400">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">Help Us Write the Next Chapter</h2>
            <p className="text-white/80 text-sm md:text-base mb-6 max-w-xl mx-auto">Every contribution, big or small, adds a new line to our story of impact. Join us in creating lasting change.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="/donate" className="px-8 py-3 bg-white text-primary-600 text-sm font-bold rounded-full hover:bg-white/90 transition-colors whitespace-nowrap">
                <i className="ri-heart-line mr-2"></i>Donate Now
              </a>
              <a href="/volunteer" className="px-8 py-3 bg-white/20 backdrop-blur-sm text-white text-sm font-bold rounded-full hover:bg-white/30 transition-colors whitespace-nowrap">
                <i className="ri-team-line mr-2"></i>Become a Volunteer
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}